import { CheckCheck, Paperclip, Undo2, Upload } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import type { MenuItem } from '../../../../components/Menu';
import { Modal } from '../../../../components/Modal';
import { DateCell } from '../../../../components/table/DateCell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { personnelLabel, usePersonnelNames } from '../../../human-resources/personnel/useStaff';
import { routeFilesApi, type RoutingMovementApi, type RoutingRouteReadApi } from '../routingApi';
import { ROUTE_KIND, type DocumentRouteItem } from '../routingTypes';
import { AttachFilesModal } from './AttachFilesModal';
import { RoutingPreviewPanel } from './RoutingPreviewPanel';

/**
 * Where a document went — every line of its route, in the order it happened.
 *
 * **Paged on the SERVER** with search and sort, because a document passed desk to desk for a
 * year grows its route without bound. The default order is the route's own sequence.
 *
 * **A dispatch shows its source's WHOLE route** — the server returns the received document's
 * lines, the dispatch line among them — so where an outgoing letter has been is one table. A
 * dispatch to a unit carries on, on its OWN lines, once that unit's Inbox takes it in.
 *
 * **The verbs are the UNIT's, decided per line by the server** (10 Oct 2026): a line offers
 * Acknowledge only to the employee it names — or, naming nobody, to someone posted to the unit it
 * went to — and Cancel and Attach files only to the unit that sent it, while it is open. A reader
 * who may do neither sees neither: who you ARE is not a state the button can explain. Both writes
 * confirm with a tick; neither asks for a password — an ordinary act of the job.
 *
 * **A forward's files** open from the line, read-only, with who attached each — the recipient's
 * question. Opening them needs the module's `download` key, as every file in the product does.
 */
export function RoutingHistoryPanel({
  recordId,
  api,
  movement,
  onChanged,
}: {
  recordId: string;
  api: RoutingRouteReadApi;
  /** Where the document can be moved from this page — the verbs, their keys and the Route form. */
  movement?: {
    api: RoutingMovementApi;
    /** `document-receiving` or `document-inbox` — whose `route` and `download` keys gate the verbs. */
    modulePrefix: 'document-receiving' | 'document-inbox';
    /** Opens the page's Route form; the panel's Create. */
    onRoute: () => void;
    /** The page's word for routing — Receiving's is Dispatch (product call, 10 Oct 2026). */
    verb: string;
    routeDisabledReason: string | null;
  };
  /** A line was acknowledged, cancelled or given a file — the page refreshes its header, banner and counts. */
  onChanged: () => void;
}) {
  const toast = useToast();

  const [showDeleted, setShowDeleted] = useState(false);
  const [confirming, setConfirming] = useState<{ kind: 'acknowledge' | 'cancel'; row: DocumentRouteItem } | null>(null);
  const [viewingFiles, setViewingFiles] = useState<DocumentRouteItem | null>(null);
  const [attaching, setAttaching] = useState<DocumentRouteItem | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchPage = useCallback(
    (query: DataTableQuery, signal: AbortSignal) =>
      api.routes(recordId, { ...query, status: showDeleted ? undefined : 'active' }, signal),
    [api, recordId, showDeleted],
  );

  const state = useDataTable<DocumentRouteItem>({ fetchPage, pageSize: 20, defaultSort: 'sequence:asc' });

  // The employees named on this page — one batch, through the masked personnel path.
  const { names, canRead } = usePersonnelNames(state.rows.map((r) => r.toPersonnelId));

  const verbs = useCallback(
    (row: DocumentRouteItem): MenuItem[] => {
      if (!movement) return [];

      const items: MenuItem[] = [];

      /*
        Files on THIS record's lines only. The Inbox shows its source's route too, but a file on a
        received document's forward is Receiving's, under Receiving's download key — the server
        refuses it here, so the menu does not offer it.
      */
      const ownsLine = movement.modulePrefix === 'document-inbox' ? row.dispatchedDocumentId === recordId : true;

      if (row.attachmentCount > 0 && ownsLine) {
        items.push({
          key: `files-${row.id}`,
          label: 'Files',
          icon: <Paperclip className="h-4 w-4" aria-hidden="true" />,
          permission: `${movement.modulePrefix}.download`,
          onClick: () => setViewingFiles(row),
        });
      }

      if (row.canAcknowledge) {
        items.push({
          key: `acknowledge-${row.id}`,
          label: 'Acknowledge',
          icon: <CheckCheck className="h-4 w-4" aria-hidden="true" />,
          permission: `${movement.modulePrefix}.route`,
          onClick: () => setConfirming({ kind: 'acknowledge', row }),
        });
      }

      if (row.canAttach) {
        items.push({
          key: `attach-${row.id}`,
          label: 'Attach files',
          icon: <Upload className="h-4 w-4" aria-hidden="true" />,
          permission: `${movement.modulePrefix}.route`,
          onClick: () => setAttaching(row),
        });
      }

      if (row.canCancel) {
        items.push({
          key: `cancel-${row.id}`,
          label: 'Cancel routing',
          icon: <Undo2 className="h-4 w-4" aria-hidden="true" />,
          permission: `${movement.modulePrefix}.route`,
          onClick: () => setConfirming({ kind: 'cancel', row }),
        });
      }

      return items;
    },
    [movement, recordId],
  );

  // The recipient's commonest verb, PROMOTED into the gutter — only where this reader may do it.
  const quick = useCallback(
    (row: DocumentRouteItem) => verbs(row).filter((item) => item.key.startsWith('acknowledge')),
    [verbs],
  );

  const columns = useMemo(() => columnsFor((id) => personnelLabel(id, names, canRead)), [names, canRead]);

  async function runConfirmed() {
    if (!confirming || !movement) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'acknowledge') {
        await movement.api.acknowledge(recordId, confirming.row.id);
        toast.success(`Acknowledged at ${confirming.row.toOrgUnitName ?? 'the unit'}.`);
      } else {
        await movement.api.cancelForward(recordId, confirming.row.id);
        toast.success(`Cancelled the routing to ${confirming.row.toOrgUnitName ?? 'the unit'}.`);
      }

      setConfirming(null);
      state.reload();
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="flex h-full min-w-0 flex-col">
      <DataTablePanel<DocumentRouteItem>
        id="document-routes"
        state={state}
        columns={columns}
        rowKey={(row) => row.id}
        searchPlaceholder="Search units, offices and remarks"
        fill
        onCreate={movement?.onRoute}
        createLabel={movement?.verb ?? 'Route'}
        createPermission={movement ? `${movement.modulePrefix}.route` : undefined}
        createDisabledReason={movement?.routeDisabledReason ?? null}
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            checked: showDeleted,
            onClick: () => setShowDeleted((v) => !v),
          },
        ]}
        rowActions={movement ? verbs : undefined}
        rowQuickActions={movement ? quick : undefined}
        otherInfo={(row) => row}
        emptyMessage="Nothing on the route yet."
        emptySearchMessage="No line of the route matches that search."
      />

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'acknowledge' ? 'Acknowledge receipt' : 'Cancel routing'}
          requireConfirm
          loading={running}
          id={`document-route-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              {confirming.kind === 'acknowledge' ? (
                <p>
                  {confirming.row.toOrgUnitName ?? 'The unit'} takes the document in. It is that unit&apos;s from now on,
                  and this line becomes part of its history — it can no longer be cancelled.
                </p>
              ) : (
                <p>
                  The routing to {confirming.row.toOrgUnitName ?? 'the unit'} is cancelled and the document stays with{' '}
                  {confirming.row.fromOrgUnitName ?? 'the unit that sent it'}. The line is kept on the route as deleted, and
                  can be seen with Show deleted.
                </p>
              )}

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}

      {viewingFiles && movement ? (
        <Modal
          open
          onClose={() => setViewingFiles(null)}
          title={`Files sent to ${viewingFiles.toOrgUnitName ?? 'the unit'}`}
          size="3xl"
          id="document-route-files-modal"
          footer={
            <Button id="document-route-files-done" variant="primary" onClick={() => setViewingFiles(null)}>
              Done
            </Button>
          }
        >
          {/* READ-ONLY — the api carries no write half. "Created by" is who attached each file. */}
          <RoutingPreviewPanel
            recordId={viewingFiles.id}
            trackingNumber={viewingFiles.receivedTrackingNumber ?? viewingFiles.dispatchedTrackingNumber ?? ''}
            api={(({ attachments, download }) => ({ attachments, download }))(routeFilesApi(movement.api, recordId, viewingFiles.id))}
            modulePrefix={movement.modulePrefix}
            isActive={viewingFiles.isActive}
            onChanged={() => undefined}
          />
        </Modal>
      ) : null}

      {attaching && movement ? (
        <AttachFilesModal
          open
          recordId={attaching.id}
          trackingNumber={attaching.receivedTrackingNumber ?? attaching.dispatchedTrackingNumber ?? ''}
          api={routeFilesApi(movement.api, recordId, attaching.id)}
          onClose={() => setAttaching(null)}
          onSaved={() => {
            setAttaching(null);
            state.reload();
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

/** The route's columns — the named employee resolved through the page's one batch. */
function columnsFor(personName: (personnelId: string | null) => string | null): DataColumn<DocumentRouteItem>[] {
  return [
    {
      key: 'kind',
      header: 'Movement',
      width: 120,
      summary: true,
      render: (row) => <TextCell value={row.kindLabel} />,
    },
    {
      // A Received line came from a unit or an OFFICE outside; every other line left a unit.
      key: 'from',
      header: 'From',
      width: 200,
      summary: true,
      render: (row) => <TextCell value={row.fromOrgUnitName ?? row.externalOffice} />,
    },
    {
      // A Dispatched line went to a unit's Inbox or an OFFICE outside; every other line reached a unit.
      key: 'to',
      header: 'To',
      width: 200,
      summary: true,
      render: (row) => <TextCell value={row.toOrgUnitName ?? row.externalOffice} />,
    },
    {
      // Blank where it named nobody — anyone posted to the unit could take it in.
      key: 'toPersonnelId',
      header: 'Addressed To',
      width: 190,
      render: (row) => <TextCell value={personName(row.toPersonnelId)} />,
    },
    {
      key: 'purpose',
      header: 'Action Requested',
      width: 150,
      render: (row) => <TextCell value={row.purposeLabel} />,
    },
    {
      key: 'routedAt',
      header: 'Moved',
      width: 170,
      sortable: true,
      render: (row) => <DateCell value={row.routedAt} />,
    },
    {
      /*
        THREE answers, each a fact: when it was taken in; that a hand-over — a forward, or a dispatch
        to a unit — is still on its way; and nothing for a dispatch to an outside office, which
        nobody acknowledges here.
      */
      key: 'acknowledgedAt',
      header: 'Acknowledged',
      width: 170,
      render: (row) =>
        row.acknowledgedAt != null ? (
          <DateCell value={row.acknowledgedAt} />
        ) : row.isActive && (row.kind === ROUTE_KIND.forwarded || (row.kind === ROUTE_KIND.dispatched && row.toOrgUnitId != null)) ? (
          <TextCell value="In transit" />
        ) : (
          <TextCell value={null} />
        ),
    },
    {
      key: 'acknowledgedByName',
      header: 'Acknowledged By',
      width: 170,
      render: (row) => <TextCell value={row.acknowledgedByName} />,
    },
    {
      key: 'attachmentCount',
      header: 'Files',
      width: 80,
      align: 'right',
      render: (row) => <TextCell value={String(row.attachmentCount)} mono numeric />,
    },
    {
      key: 'remarks',
      header: 'Remarks',
      width: 260,
      render: (row) => <TextCell value={row.remarks} />,
    },
    {
      key: 'status',
      header: 'Status',
      width: 110,
      status: true,
      render: (row) =>
        row.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
    },
  ];
}
